import { MY_ACTIVITY_PAGE } from 'constants/pages';
import Button, { ButtonLink } from 'components/Button';
import classNames from 'styles/utils/classNames';

export type BookingOutcome = 'PENDING' | 'CONFIRMED' | 'FAILED' | 'NEEDS_REVIEW';

interface Props {
  /** Follows the order as it settles; the step opens on PENDING right after checkout. */
  outcome: BookingOutcome;
  /** Resets the flow back to the slot grid so the player can book again. */
  onBookMore: () => void;
  className?: string;
}

const COPY: Record<BookingOutcome, { title: React.ReactNode; body: string }> = {
  PENDING: {
    title: (
      <>
        We&rsquo;re booking
        <br />
        your court
      </>
    ),
    body: "You will receive a confirmation email once it's booked.",
  },
  CONFIRMED: {
    title: 'Your court is booked',
    body: 'The club confirmed it and your card has been charged. A confirmation email is on its way.',
  },
  FAILED: {
    title: "We couldn't book that court",
    body: 'The club could not take the booking. Your card was not charged. Try another time or court.',
  },
  NEEDS_REVIEW: {
    title: 'Checking with the club',
    body: "Something didn't line up and our team is looking into it. Your card is held, not charged. We'll email you shortly.",
  },
};

// The booking settles out of band, so this step follows the order rather than claiming success.
// The 76px headroom is the design's empty modal header: no back arrow, so padding instead.
export default function BookingConfirmedStep({ outcome, onBookMore, className }: Props) {
  const copy = COPY[outcome];
  return (
    <div className={classNames('flex flex-col', className)}>
      <div className="flex flex-col items-center gap-ds-2xl px-ds-3xl pb-16 pt-[4.75rem]">
        {outcome === 'CONFIRMED' ? (
          <span className="relative flex size-[7.5rem] items-center justify-center">
            <img
              src="/images/app/success-3d/disc.png"
              alt=""
              aria-hidden
              className="size-[7.5rem]"
            />
            <img
              src="/images/app/success-3d/check.svg"
              alt=""
              aria-hidden
              className="absolute size-14"
            />
          </span>
        ) : (
          <img
            src="/images/court-booking/booking-hourglass.png"
            alt=""
            aria-hidden
            width={120}
            height={120}
            className="size-[7.5rem]"
          />
        )}
        <div className="flex max-w-[30rem] flex-col items-center gap-ds-xl text-center">
          <p className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {copy.title}
          </p>
          <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {copy.body}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-ds-xl border-t border-color-border-input-lightmode px-ds-3xl py-5 dark:border-color-border-input-darkmode">
        <ButtonLink href={MY_ACTIVITY_PAGE} variant="secondary" size="lg" label="View activity" />
        <Button variant="primary" size="lg" label="Book more" onClick={onBookMore} />
      </div>
    </div>
  );
}
